import {action, computed, observable} from 'mobx';
import {
    PVAKeyValueDataService,
    SurveyVersionDataService,
    OrganizationDataService,
    SurveyDataService,
    NotificationService,
    TeamDataService,
    UserDataService,
    SecondaryReportDataService,
    SurveyReportTypeDataService,
    Auth
} from '../services';
import {
    Survey,
    SurveyVersion,
    Organization,
    Team,
    User,
    SurveyReportType,
    SecondaryReport,
    PVAKeyValue,
    OrganizationSurvey,
    UserReportTypeInfo,
    Style, UserPermissions
} from '../models';
import { Roles } from './Constants';

export class FilterStore {
    @observable survey: Survey | undefined;
    @observable surveys: Survey[];
    @observable surveyVersion: SurveyVersion | undefined;
    @observable surveyVersions: SurveyVersion[];
    @observable surveyReportType: SurveyReportType | undefined;
    @observable surveyReportTypes: SurveyReportType[];
    @observable teams: Team[];
    @observable team: Team | undefined;
    @observable teamName: string;
    @observable teamId: number;
    @observable organization: Organization;
    @observable organizationId: number;
    @observable organizations: Organization[];
    @observable users: User[];
    @observable secondaryReports: SecondaryReport[];
    @observable keyValues: PVAKeyValue[];
    @observable noSurveys: boolean = false;
    @observable userReportTypes: UserReportTypeInfo[];
    
    @observable individualReportUserId: number | undefined;
    @observable individualReportUserData: any | undefined;
    @observable interactionReportUserId: number | undefined;
    @observable interactionReportUserData: any | undefined;
    @observable pvaStyles: Style[] | undefined;
    @observable pvaStylesSurveyVersionId: number | undefined;

    private organizationDataService: OrganizationDataService;
    private surveyDataService: SurveyDataService;
    private surveyVersionDataService: SurveyVersionDataService;
    private teamDataService: TeamDataService;
    private userDataService: UserDataService;
    private secondaryReportDataService: SecondaryReportDataService;
    private surveyReportTypeDataService: SurveyReportTypeDataService;
    private pvaKeyValueDataService: PVAKeyValueDataService;
    
    constructor(notificationService: NotificationService) {
        this.teamId = 0;
        this.teamName = 'No Team Selected';
        this.users = [];
        this.surveyVersions = [];
        this.secondaryReports = [];
        this.organizationDataService = new OrganizationDataService(notificationService);
        this.surveyDataService = new SurveyDataService(notificationService);
        this.surveyVersionDataService = new SurveyVersionDataService(notificationService);
        this.secondaryReportDataService = new SecondaryReportDataService(notificationService);
        this.teamDataService = new TeamDataService(notificationService);
        this.userDataService = new UserDataService(notificationService);
        this.surveyReportTypeDataService = new SurveyReportTypeDataService(notificationService);
        this.pvaKeyValueDataService = new PVAKeyValueDataService(notificationService);
        this.keyValues = [];
    }

    public SetUserSurveyReportType(reportTypes: UserReportTypeInfo[]) {
        this.userReportTypes = reportTypes;

        if (this.surveyReportTypes && !this.surveyReportType) {
            this.surveyReportType = this.getDefaultOrFirstSurveyReportType();
        }
    }

    @action setSurveyReportType(surveyReportType: SurveyReportType) {
        this.surveyReportType = surveyReportType;
    }

    @action setSurveyVersion(surveyVersion: SurveyVersion) {
        this.surveyVersion = surveyVersion;
        if (this.surveyVersion) {
            this.secondaryReportDataService.getAllBySurveyVersion(this.surveyVersion).then((reports: SecondaryReport[]) => {
                this.secondaryReports = reports;
            });
        }
    }

    private getDefaultOrFirstSurveyReportType() {
        if (this.userReportTypes && this.survey) {
            var s = this.survey;
            var ind = this.userReportTypes.findIndex(x => x.surveyID === s.id);
            if (ind > -1) {
                var userReportType = this.userReportTypes[ind];
                ind = this.surveyReportTypes.findIndex(x => x.id === userReportType.surveyReportTypeID);
                if (ind > -1) {
                    return this.surveyReportTypes[ind];
                }
            }
        }
        var defSRTInd = this.surveyReportTypes.findIndex((srt: SurveyReportType) => {
            return srt.default;
        });
        if (defSRTInd > -1) {
            return this.surveyReportTypes[defSRTInd];
        } else if (this.surveyReportTypes.length > 0) {
            return this.surveyReportTypes[0];
        }
        return undefined;
    }

    private getSurveyReportTypes() {
        if (!this.survey || !this.organization) {
            return;
        }
        
        this.surveyReportTypeDataService.getBySurvey(this.survey.id, this.organization.id).then((surveyReportTypes: SurveyReportType[]) => {
            this.surveyReportTypes = surveyReportTypes || [];
            if (surveyReportTypes && surveyReportTypes.length > 0) {
                if (this.surveyReportType) {
                    var id = this.surveyReportType.id;
                    var stillGoodReportType = surveyReportTypes.find((x: SurveyReportType) => {
                        return x.id === id;
                    });
                    if (!stillGoodReportType) {
                        this.surveyReportType = this.getDefaultOrFirstSurveyReportType();
                    }
                } else {
                    this.surveyReportType = this.getDefaultOrFirstSurveyReportType();
                }
            } else {
                this.surveyReportType = undefined;
            }
        });        
    }

    private setSurveyVersionPublishedOrFirstAvailable() {
        for (var i: number = 0; i < this.surveyVersions.length; i++) {
            if (this.surveyVersions[i].published) {
                this.setSurveyVersion(this.surveyVersions[i]);
                return;
            }
        }

        // There was no published survey version, set first available
        if (this.surveyVersions.length > 0) {
            this.setSurveyVersion(this.surveyVersions[0]);
        }
    }

    private getSurveyVersions() {
        if (!this.survey) {
            return;
        }

        this.surveyVersionDataService.getAllBySurvey(this.survey).then((surveyVersions: SurveyVersion[]) => {
            surveyVersions.sort((a: SurveyVersion, b: SurveyVersion) => {
                if (a.version > b.version) {
                    return -1;
                } else if (a.version < b.version) {
                    return 1;
                }
                return 0;
            });
            this.surveyVersions = surveyVersions;
            var ind = this.getOrgSurveyVersionInd();
            if (ind > -1) {
                this.setSurveyVersion(surveyVersions[ind]);
            } else {
                this.setSurveyVersionPublishedOrFirstAvailable();
            }
        });
    }

    private getOrgSurveyVersionInd() {
        if (!this.survey || !this.organization) {
            return -1;
        }

        var ind: number = -1;
        // if org has a default set
        if (this.organization && this.organization.organizationSurveys) {
            var os = this.organization.organizationSurveys.find((os2: OrganizationSurvey) => {
                if (this.survey) {
                    return os2.surveyID === this.survey.id;
                }
                return false;
            });
            if (os && os.surveyVersionID) {
                ind = this.surveyVersions.findIndex((sv: SurveyVersion) => {
                    if (os) {
                        return sv.id === os.surveyVersionID;
                    }
                    return false;
                });
            }
        }
        if (ind === -1) {
            this.surveyVersions.map((surveyVersionA: SurveyVersion, index: number) => {
                if (surveyVersionA.active) {
                    ind = index;
                }
            });
        }
        return ind;
    }

    @action setSurvey(survey: Survey | undefined) {
        this.survey = survey;
        this.surveyVersions = [];       
        this.pvaStyles = undefined;
        this.pvaStylesSurveyVersionId = undefined;

        this.getSurveyReportTypes();

        if (this.survey !== undefined) {
            this.getSurveyReportTypes();
            this.getSurveyVersions();
            this.noSurveys = false;
        } else {
            this.noSurveys = true;
        }
    }

    private getSurveys() {
        if (this.organization) {
            this.surveyDataService.getAllByOrganization(this.organization).then((surveys: Survey[]) => {
                surveys.sort((a: Survey, b: Survey) => {
                    if (a.id > b.id) {
                        return 1;
                    } else if (a.id < b.id) {
                        return -1;
                    }
                    return 0;
                });
                this.surveys = surveys;
                if (this.surveys.length > 0) {
                    this.setSurvey(this.surveys[0]);
                }
            });
        }
    }

    @action async setOrganization(organization: Organization, initialTeam: number = 0) {
        this.organization = organization;
        this.organizationId = organization.id;
        this.teamId = initialTeam;
        this.team = undefined;
        this.individualReportUserId = undefined;
        this.individualReportUserData = undefined;
        this.interactionReportUserId = undefined;
        this.interactionReportUserData = undefined;
        this.pvaStyles = undefined;
        this.pvaStylesSurveyVersionId = undefined;
        this.keyValues = [];

        this.getSurveys();
        
        const teams = await this.teamDataService.getByOrganization(organization);
        this.teams = teams || [];
        {
            const auth = new Auth();
            const managedTeams = auth.managedTeams();

            this.teams = this.teams.filter(team => {
                if (
                    auth.userHasRole(Roles.OrgAdmin) ||
                    auth.userHasRole(Roles.SiteAdmin) ||
                    auth.userHasRole(Roles.SuperAdmin) ||
                    auth.userHasRole(Roles.PonderaAssociate)
                ) {
                    return true;
                }
                return managedTeams.includes(team.id.toString());
            });
        }
        this.users = [];

        this.keyValues = await this.pvaKeyValueDataService.getAllByOrganization(organization);
    }

    @action async getOrganizations(cachedOrg: Organization | undefined) {
        const organizations = await this.organizationDataService.getAllByUser();
        // we're going to find the org in this collection to ensure we don't have stale data (e.g. from pulling the org out of localstorage)
        const refreshedOrg = organizations.find((x:Organization) => x.id === cachedOrg!.id);
        if (refreshedOrg) {
            cachedOrg = refreshedOrg;
        }

        this.organizations = organizations.sort(this.sortByName);
        await this.setOrganization(cachedOrg!);
    }

    private sortByName(a: any, b: any) {
        if (a.name > b.name) {
            return 1;
        }
        if (a.name < b.name) {
            return -1;
        }
        return 0;
    }

    @action async setTeam(team: Team | undefined) {
        this.team = team;

        this.individualReportUserId = undefined;
        this.individualReportUserData = undefined;
        this.interactionReportUserId = undefined;
        this.interactionReportUserData = undefined;

        if (team) {
            this.teamId = team.id;
            this.teamName = team.name;
            this.users = await this.userDataService.getByTeam(team);
        }
    }

    @action async setTeamID(teamId: number) {
        this.teamId = teamId;
        this.team = undefined;

        this.individualReportUserId = undefined;
        this.individualReportUserData = undefined;
        this.interactionReportUserId = undefined;
        this.interactionReportUserData = undefined;
        if (teamId === 0) {
            // - select -
            this.users = [];
            this.teamName = 'No Team Selected';
        } else if (teamId === -100) {
            // Get all users
            this.users = await this.userDataService.getByOrganization(this.organization.id);
            this.teamName = 'All Users';
        } else if (teamId === -200) {
            // Get all candidates
            this.users = await this.userDataService.getByOrganizationLightCandidates(this.organization.id);
            this.teamName = 'All Candidates';
        } else if (teamId === -300) {
            // Get all former employees
            this.users = await this.userDataService.getByOrganizationLightFormer(this.organization.id);
            this.teamName = 'All Former Employees';
        }
    }
}


// WEBPACK FOOTER //
// ./src/stores/FilterStore.tsx